import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  AccountCircle,
  LocationOn,
  ExpandMore,
  ExpandLess,
  ArrowBack,
} from '@mui/icons-material';
import { useAuth } from '@features/auth/hooks/useAuth';
import { usePermissions } from '@shared/hooks/usePermissions';
import { MENU_ITEMS } from '../../config/navigation';

const drawerWidth = 260;

export const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});
  const { user, logout } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleGroupToggle = (key: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // ── Recursive menu renderer ───────────────────────────
  // Supports unlimited nesting depth. `depth` controls indentation.
  const renderMenuItems = (items: typeof MENU_ITEMS, depth = 0): React.ReactNode => {
    return items
      .filter(item => {
        // No restrictions → visible to all authenticated users
        if (!item.roles && !item.permissions) return true;
        // Role matches → show
        if (item.roles && user && item.roles.includes(user.role)) return true;
        // Permission matches (any one) → show
        if (item.permissions && hasAnyPermission(item.permissions)) return true;
        return false;
      })
      .map(item => {
        // Group: item has children
        if (item.children) {
          const groupKey = item.title + '-' + depth;
          const isOpen = openGroups[groupKey] || false;

          return (
            <Box key={groupKey}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleGroupToggle(groupKey)}
                  sx={{
                    borderRadius: 2,
                    pl: 1 + depth * 1.5,
                    color: '#94A3B8',
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: '#1e293b', color: 'white' },
                  }}
                >
                  {item.icon && (
                    <ListItemIcon sx={{ color: '#94A3B8', minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: depth === 0 ? '0.9375rem' : '0.875rem',
                    }}
                  />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>

              {isOpen && (
                <List disablePadding sx={{ pl: depth === 0 ? 1 : 1.5 }}>
                  {renderMenuItems(item.children, depth + 1)}
                </List>
              )}
            </Box>
          );
        }

        // Leaf: item has a path
        const isActive = Boolean(item.path) && location.pathname === item.path;
        return (
          <ListItem key={item.path || item.title} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => item.path && handleMenuItemClick(item.path)}
              selected={isActive}
              sx={{
                borderRadius: 2,
                pl: 1 + depth * 1.5,
                color: isActive ? '#ffffff' : '#94A3B8',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, #14b8a6, #0d9488)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)',
                  '&:hover': { background: 'linear-gradient(90deg, #0d9488, #0f766e)' },
                  '& .MuiListItemIcon-root': { color: 'white' },
                },
                '&:hover': {
                  backgroundColor: isActive ? 'transparent' : '#1e293b',
                  color: 'white',
                },
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ color: isActive ? 'white' : '#94A3B8', minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontWeight: isActive ? 600 : 500,
                  fontSize: depth === 0 ? '0.9375rem' : '0.875rem',
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      });
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0f172a' }}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 3,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box
          sx={{
            width: 70,
            height: 70,
            borderRadius: '18px',
            background: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FDD835 0%, #F9A825 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <LocationOn
              sx={{
                fontSize: 24,
                color: '#0891b2',
                mb: -0.5,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: '#0891b2',
                letterSpacing: '2px',
                fontSize: '11px',
              }}
            >
              KAPS
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <List sx={{ px: 2, py: 2, flex: 1 }}>
        {renderMenuItems(MENU_ITEMS)}
      </List>
    </Box>
  );


  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Mobile Menu Button */}
      <IconButton
        color="inherit"
        onClick={handleDrawerToggle}
        sx={{
          display: { md: 'none' },
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1201,
          bgcolor: 'white',
          boxShadow: 2,
          '&:hover': {
            bgcolor: '#f8fafc',
          },
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#0f172a',
              color: 'white',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#0f172a',
              color: 'white',
              borderRight: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area with Fixed Header and Footer */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Fixed Header */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e2e8f0',
            color: '#1e293b',
          }}
        >
          <Toolbar sx={{ justifyContent: 'flex-end', minHeight: { xs: 56, sm: 64 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, color: '#64748b' }}>
                {user?.name || 'User'}
              </Typography>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{
                  p: 0,
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                    background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                    width: 40,
                    height: 40,
                    fontWeight: 600,
                  }}
                >
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Scrollable Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            backgroundColor: '#f8fafc',
            overflow: 'auto',
            p: { xs: 2, sm: 3 },
          }}
        >
          {/* Back arrow — hidden on dashboard */}
          {location.pathname !== '/dashboard' && (
            <IconButton
              aria-label="Go back"
              size="small"
              onClick={handleGoBack}
              sx={{ mb: 1.5, alignSelf: 'flex-start' }}
            >
              <ArrowBack />
            </IconButton>
          )}

          <Outlet />
        </Box>

        {/* Fixed Footer */}
        <Box
          component="footer"
          sx={{
            borderTop: '1px solid #e2e8f0',
            bgcolor: 'white',
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              © {new Date().getFullYear()} KAPS Admin. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  cursor: 'pointer',
                  '&:hover': { color: '#14b8a6' },
                  transition: 'color 0.2s',
                }}
              >
                Help
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  cursor: 'pointer',
                  '&:hover': { color: '#14b8a6' },
                  transition: 'color 0.2s',
                }}
              >
                Privacy
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  cursor: 'pointer',
                  '&:hover': { color: '#14b8a6' },
                  transition: 'color 0.2s',
                }}
              >
                Terms
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
